import React, { ChangeEvent } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { US_STATES } from 'src/constants/usStates';
import { ValidationErrors } from 'src/constants/validations';
import {
  SignupCompanyInfoBlock,
  SignupCompanyInfoFormValues,
} from '../../controllers/SignupCompanyInfoViewController';
import { SignupFormOptionsResponse } from '../../types';
import { noticeSx } from '../../helpers/sx';

type Props = {
  formValues: SignupCompanyInfoFormValues;
  options: SignupFormOptionsResponse;
  optionsLoading: boolean;
  errors: ValidationErrors;
  formError: string | null;
  submitting: boolean;
  /** A refusal the form cannot be pressed through, or null while it still can be. */
  blocked: SignupCompanyInfoBlock | null;
  onChange: (e: ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => void;
  onWebsiteBlur: () => void;
  onSubmit: () => void;
  onLogIn: () => void;
  onStartOver: () => void;
};

// Picked from the same list the onboarding brand form offers, and for the same reason: the carriers match
// the address on the brand, and a typed state is a rejected registration found out days later.
const STATE_OPTIONS = US_STATES.map(state => ({ value: state.code, label: state.name }));

export const SignupCompanyInfoView: React.FC<Props> = ({
  formValues,
  options,
  optionsLoading,
  errors,
  formError,
  submitting,
  blocked,
  onChange,
  onWebsiteBlur,
  onSubmit,
  onLogIn,
  onStartOver,
}) => {
  const field = (
    name: keyof SignupCompanyInfoFormValues,
    label: string,
    extra: object = {},
    hint = '',
  ) => (
    <TextField
      fullWidth
      name={name}
      label={label}
      value={formValues[name]}
      onChange={onChange}
      error={errors[name] != null}
      helperText={errors[name] ?? hint}
      {...extra}
    />
  );

  const select = (
    name: keyof SignupCompanyInfoFormValues,
    label: string,
    items: { value: string; label: string }[],
  ) => (
    <TextField
      select
      fullWidth
      name={name}
      label={label}
      value={formValues[name]}
      onChange={onChange}
      error={errors[name] != null}
      helperText={errors[name]}
    >
      {items.map(item => (
        <MenuItem key={item.value} value={item.value}>
          {item.label}
        </MenuItem>
      ))}
    </TextField>
  );

  // Held back until the options answer: the industry field, and with it the layout, depends on them.
  if (optionsLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  // Three to a row, or two rows of two once the industry is asked too.
  const detailWidth = options.verticals != null ? 6 : 4;

  return (
    <Box maxWidth={760} mx="auto">
      <Typography variant="h4" fontWeight="fontWeightSemiBold" gutterBottom>
        Tell us about your company
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Carriers require these details before your number can send messages, so they are collected once, up
        front.
      </Typography>

      {formError != null && (
        <Alert severity="error" sx={noticeSx}>
          {formError}
          {/* Named as actions rather than left to the sentence: "log in instead" is advice on a page with
              nothing to log in from. */}
          {blocked != null && (
            <Stack direction="row" spacing={1} mt={1}>
              <Button size="small" variant="contained" onClick={onLogIn}>
                Log in
              </Button>
              {/* Not for our own account, which is finished: starting again would raise a second beside it. */}
              {blocked === 'emailTaken' && (
                <Button size="small" variant="text" onClick={onStartOver}>
                  Start a new signup
                </Button>
              )}
            </Stack>
          )}
        </Alert>
      )}

      {/*
        Two groups — what the company is and where it is — told apart by the space between them and nothing
        else. Every row divides the twelve columns exactly: a field left over stands beside a gap, and the
        group ends before anything can move up into it.
      */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>{field('companyName', 'Legal company name')}</Grid>
        <Grid item xs={12} md={6}>{field('ein', 'EIN')}</Grid>
        {/* Under the legal name because it opens as a copy of it. */}
        <Grid item xs={12} md={detailWidth}>
          {field('displayName', 'Display name (optional)', {}, 'How you are known to the people you text.')}
        </Grid>
        <Grid item xs={12} md={detailWidth}>{select('entityType', 'Entity type', options.entityTypes)}</Grid>
        {options.verticals != null && (
          <Grid item xs={12} md={detailWidth}>{select('vertical', 'Industry', options.verticals)}</Grid>
        )}
        <Grid item xs={12} md={detailWidth}>
          {field('website', 'Website (optional)', { onBlur: onWebsiteBlur })}
        </Grid>
      </Grid>

      {/*
        Three times the space between rows inside a group, because with the group headings gone this gap is
        the only thing saying where one question ends and the next begins. A wrapper rather than a margin on
        the container: that one carries a negative top margin of its own and would eat half of it.
      */}
      <Box mt={6}>
        <Grid container spacing={2}>
          <Grid item xs={12}>{field('street', 'Street')}</Grid>
          <Grid item xs={12} md={5}>{field('city', 'City')}</Grid>
          <Grid item xs={6} md={3}>{select('state', 'State', STATE_OPTIONS)}</Grid>
          <Grid item xs={6} md={4}>{field('postalCode', 'Postal code')}</Grid>
        </Grid>
      </Box>

      <Box mt={4} display="flex" justifyContent="flex-end">
        <Button variant="contained" size="large" disabled={submitting} onClick={onSubmit}>
          {submitting ? 'Saving…' : 'Continue'}
        </Button>
      </Box>
    </Box>
  );
};
